import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from 'lucide-react';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  image_url: string | null;
  created_at: string;
}

const Blog = () => {
  const { t } = useLanguage();
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const { data, error } = await supabase
        .from('blogs')
        .select('id, title, slug, excerpt, image_url, created_at')
        .eq('published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBlogPosts(data || []);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <main>
      {/* Hero */}
      <section className="bg-hero-gradient text-white py-12 md:py-20 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-bold font-['Playfair_Display'] mb-3 md:mb-4">
            {t('blogTitle')}
          </h1>
          <p className="text-base md:text-xl text-white/90">
            {t('blogSubtitle')}
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-8 md:py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : blogPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm md:text-base text-muted-foreground">No blog posts available yet.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {blogPosts.map((post) => (
                <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-shadow">
                  {post.image_url && (
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="p-4 md:p-6">
                    <div className="flex items-center text-xs md:text-sm text-muted-foreground mb-2 md:mb-3">
                      <Calendar className="w-3 h-3 md:w-4 md:h-4 mr-2" />
                      {formatDate(post.created_at)}
                    </div>
                    <h2 className="text-base md:text-xl font-bold mb-2 md:mb-3 line-clamp-2">{post.title}</h2>
                    {post.excerpt && (
                      <p className="text-xs md:text-base text-muted-foreground mb-3 md:mb-4 line-clamp-3">{post.excerpt}</p>
                    )}
                    <Link to={`/blog/${post.slug}`}>
                      <Button variant="outline" className="w-full text-xs md:text-sm">
                        {t('readMore')}
                      </Button>
                    </Link>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default Blog;
